import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './PartnerOnboardingModal.css';

const PartnerOnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Partner data
  const [partnerName, setPartnerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [partnerId, setPartnerId] = useState(null);
  
  // Partner contact information
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerPhone, setPartnerPhone] = useState('');
  const [partnerContactPerson, setPartnerContactPerson] = useState('');
  
  // Agency settings
  const [noDirectClientMessages, setNoDirectClientMessages] = useState(false);
  const [agencyContactEmail, setAgencyContactEmail] = useState('');
  const [agencyContactPhone, setAgencyContactPhone] = useState('');
  const [agencyContactPerson, setAgencyContactPerson] = useState('');
  
  // Step 2: Form data
  const [formName, setFormName] = useState('');
  const [formSourceId, setFormSourceId] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formId, setFormId] = useState(null);
  const [previewSlug, setPreviewSlug] = useState('');
  
  // Step 3: Template data
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [copiedTemplates, setCopiedTemplates] = useState([]);
  
  // Load templates when reaching step 3
  useEffect(() => {
    if (currentStep === 3 && availableTemplates.length === 0) {
      loadTemplates();
    }
  }, [currentStep]);

  // Auto-generate form name, slug, and source ID from company name
  useEffect(() => {
    if (companyName) {
      // Generate form name from company name
      const generatedFormName = companyName.trim() + ' Form';
      setFormName(generatedFormName);
      
      // Generate slug
      const slug = companyName.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/^-+|-+$/g, '');
      setPreviewSlug(slug);
      
      // Auto-generate source ID with timestamp to make it unique
      const baseSourceId = 'leads_' + companyName.toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/^_+|_+$/g, '');
      
      // Add timestamp to make source_id unique
      const timestamp = Date.now();
      const sourceId = `${baseSourceId}_${timestamp}`;
      setFormSourceId(sourceId);
    } else {
      setFormName('');
      setPreviewSlug('');
      setFormSourceId('');
    }
  }, [companyName]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Don't filter by category - get all templates
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/contracts/documents/search`, {
        params: {
          // No category filter - we want all templates regardless of category
        }
      });
      
      console.log('All templates from API:', response.data.templates);
      
      if (response.data.status === 'success') {
        // Filter to only show base templates (those with "TEMPLATE" in title and no partner_id)
        const allTemplates = response.data.templates || [];
        
        // Log each template's details
        console.log('===== TEMPLATE DETAILS =====');
        allTemplates.forEach((template, index) => {
          console.log(`Template ${index}:`, {
            title: template.title,
            partner_id: template.partner_id,
            partner_id_type: typeof template.partner_id,
            partner_name: template.partner_name,
            assigned_to: template.assigned_to,
            raw: template
          });
        });
        console.log('===== END TEMPLATE DETAILS =====');
        
        const baseTemplates = allTemplates.filter(template => {
          const hasTemplateInTitle = template.title && template.title.toUpperCase().includes('TEMPLATE');
          // Check for various "empty" partner_id values
          const hasNoPartnerId = !template.partner_id || 
                                 template.partner_id === null || 
                                 template.partner_id === 'null' ||
                                 template.partner_id === '' ||
                                 template.partner_id === undefined;
          
          console.log(`Filtering "${template.title}": hasTemplateInTitle=${hasTemplateInTitle}, hasNoPartnerId=${hasNoPartnerId}, partner_id="${template.partner_id}" (${typeof template.partner_id})`);
          return hasTemplateInTitle && hasNoPartnerId;
        });
        
        console.log('Filtered base templates:', baseTemplates);
        console.log('Number of filtered templates:', baseTemplates.length);
        setAvailableTemplates(baseTemplates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Submit = async () => {
    if (!companyName.trim()) {
      toast.error('Legal company name is required');
      return;
    }
    
    // If no direct client messages is checked, validate agency contact info
    if (noDirectClientMessages) {
      if (!agencyContactEmail.trim()) {
        toast.error('Agency contact email is required when blocking direct client messages');
        return;
      }
      if (!agencyContactPerson.trim()) {
        toast.error('Agency contact person is required when blocking direct client messages');
        return;
      }
    }

    try {
      setLoading(true);
      const response = await axios.post(API_ENDPOINTS.PARTNERS.ADD, {
        name: partnerName.trim(),
        company_name: companyName.trim() || null,
        partner_email: partnerEmail.trim() || null,
        partner_phone: partnerPhone.trim() || null,
        partner_contact_person: partnerContactPerson.trim() || null,
        no_direct_client_messages: noDirectClientMessages,
        agency_contact_email: agencyContactEmail.trim() || null,
        agency_contact_phone: agencyContactPhone.trim() || null,
        agency_contact_person: agencyContactPerson.trim() || null
      });
      
      if (response.data && response.data.id) {
        setPartnerId(response.data.id);
        toast.success('Partner created successfully');
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Error creating partner:', error);
      toast.error('Failed to create partner');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    if (!formName.trim()) {
      toast.error('Form could not be generated - please go back and enter company name');
      return;
    }

    try {
      setLoading(true);
      
      // Auto-generate slug from name if not provided
      const slug = formName.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/form/gi, '')
        .replace(/^-+|-+$/g, '');
      
      console.log('Checking for existing form with slug:', slug);
      
      // Check if form already exists with this slug
      let formExists = false;
      try {
        const existingForm = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/forms/by-slug/${slug}`);
        console.log('Existing form check result:', existingForm.data);
        if (existingForm.data && (existingForm.data.id || existingForm.data._id)) {
          // Form already exists, just proceed to next step
          setFormId(existingForm.data.id || existingForm.data._id);
          toast.success('Form already exists, proceeding to next step');
          setCurrentStep(3);
          formExists = true;
        }
      } catch (checkError) {
        // 404 means form doesn't exist, which is fine
        console.log('Form does not exist yet, will create new one');
      }
      
      if (formExists) {
        setLoading(false);
        return;
      }
      
      // Default fields for new forms (matches SnapWave form structure)
      const defaultFields = [
        { name: 'First_Legal_Name', label: 'First Legal Name', type: 'text', section: 'Personal Information', required: true, placeholder: 'First Legal Name *' },
        { name: 'Last_Legal_Name', label: 'Last Legal Name', type: 'text', section: 'Personal Information', required: true, placeholder: 'Last Legal Name *' },
        { name: 'Email_Address', label: 'Email Address', type: 'email', section: 'Personal Information', required: true, placeholder: 'Email Address *' },
        { name: 'Phone', label: 'Phone Number', type: 'tel', section: 'Personal Information', required: true, placeholder: 'Phone Number *' },
        { name: 'DOB', label: 'Date of Birth', type: 'dob', section: 'Personal Information', required: true, placeholder: 'Date of Birth (MMDDYYYY) *' },
        { name: 'IG_Username', label: 'Instagram Username', type: 'text', section: 'Social Media', required: false, placeholder: 'Instagram Username (without @)' },
        { name: 'TT_Username', label: 'TikTok Username', type: 'text', section: 'Social Media', required: false, placeholder: 'TikTok Username (without @)' },
        { name: 'YT_Username', label: 'YouTube Username', type: 'text', section: 'Social Media', required: false, placeholder: 'YouTube Username (without @)' },
        { name: 'Snap_Username', label: 'Snapchat Username', type: 'text', section: 'Social Media', required: false, placeholder: 'Snapchat Username (without @)' },
        { name: 'Want_Snapchat', label: 'Do you want to be on Snapchat?', type: 'select', section: 'Platform Preferences', required: true, placeholder: 'Please select...', options: ['Yes', 'No', 'Unsure'] },
        { name: 'Want_Subscription', label: 'Do you want to do subscription content?', type: 'select', section: 'Platform Preferences', required: true, placeholder: 'Please select...', options: ['Yes', 'No', 'Unsure'] }
      ];
      
      const formData = {
        name: formName.trim(),
        source_id: formSourceId.trim(),
        slug: slug,
        description: formDescription.trim() || '',
        partner_id: partnerId,
        is_active: formActive,
        submit_button_text: 'Submit',
        fields: defaultFields
      };
      
      console.log('Creating form with data:', formData);
      
      const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/forms`, formData);
      
      console.log('Form creation response:', response.data);
      
      if (response.data.status === 'success') {
        setFormId(response.data.form_id || response.data.id);
        toast.success(`Form created! URL: /apply/${slug}`);
        setCurrentStep(3);
      }
    } catch (error) {
      console.error('Error creating form:', error);
      console.error('Error response:', error.response?.data);
      
      // If error is "already exists", just proceed
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.includes('already exists')) {
        toast.success('Form already exists, proceeding to next step');
        setCurrentStep(3);
      } else {
        toast.error(errorDetail || 'Failed to create form');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template to copy');
      return;
    }

    try {
      setLoading(true);
      const copied = [];
      
      for (const template of selectedTemplates) {
        // Replace TEMPLATE with partner name in title
        let newTitle = template.title.replace(/TEMPLATE/gi, partnerName);
        
        // Replace COMPANY with company name in content
        let newContent = template.content.replace(/COMPANY/g, companyName);
        
        const templateData = {
          title: newTitle,
          content: newContent,
          category: template.category || 'general',
          type: template.type || 'general',
          description: template.description || '',
          tags: template.tags || [],
          assigned_to: 'template',
          partner_id: partnerId,
          partner_name: partnerName
        };
        
        const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/contracts/documents/save`, templateData);
        
        if (response.data.status === 'success') {
          copied.push({
            id: response.data.template_id,
            title: newTitle,
            original: template.title
          });
        }
      }
      
      setCopiedTemplates(copied);
      toast.success(`Successfully copied ${copied.length} template(s)`);
      setCurrentStep(4);
    } catch (error) {
      console.error('Error copying templates:', error);
      toast.error('Failed to copy templates');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4Submit = async () => {
    try {
      setLoading(true);
      
      // Templates are already assigned in step 3, so we just need to confirm
      toast.success('Partner onboarding completed successfully!');
      
      // Call the completion callback
      if (onComplete) {
        onComplete();
      }
      
      // Close modal
      handleClose();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelection = (template) => {
    setSelectedTemplates(prev => {
      const isSelected = prev.find(t => t.id === template.id);
      if (isSelected) {
        return prev.filter(t => t.id !== template.id);
      } else {
        return [...prev, template];
      }
    });
  };

  const handleClose = () => {
    // Reset all state
    setCurrentStep(1);
    setPartnerName('');
    setCompanyName('');
    setPartnerId(null);
    setPartnerEmail('');
    setPartnerPhone('');
    setPartnerContactPerson('');
    setNoDirectClientMessages(false);
    setAgencyContactEmail('');
    setAgencyContactPhone('');
    setAgencyContactPerson('');
    setFormName('');
    setFormSourceId('');
    setFormDescription('');
    setFormActive(true);
    setFormId(null);
    setAvailableTemplates([]);
    setSelectedTemplates([]);
    setCopiedTemplates([]);
    setLoading(false);
    
    onClose();
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (!companyName.trim()) return false;
        if (noDirectClientMessages) {
          return agencyContactEmail.trim() && agencyContactPerson.trim();
        }
        return true;
      case 2:
        return previewSlug.length > 0;
      case 3:
        return selectedTemplates.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="overlay-partner-onboarding" onClick={(e) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    }}>
      <div className="modal-partner-onboarding" onClick={(e) => e.stopPropagation()}>
        <div className="header-partner-onboarding">
          <h2>Partner Onboarding</h2>
          <button onClick={handleClose} className="close-button-partner-onboarding">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="progress-partner-onboarding">
          <div className={`progress-step-partner-onboarding ${currentStep >= 1 ? 'active-partner-onboarding' : ''} ${currentStep > 1 ? 'completed-partner-onboarding' : ''}`}>
            <div className="step-number-partner-onboarding">{currentStep > 1 ? <FontAwesomeIcon icon={faCheck} /> : '1'}</div>
            <div className="step-label-partner-onboarding">Partner</div>
          </div>
          <div className="progress-line-partner-onboarding"></div>
          <div className={`progress-step-partner-onboarding ${currentStep >= 2 ? 'active-partner-onboarding' : ''} ${currentStep > 2 ? 'completed-partner-onboarding' : ''}`}>
            <div className="step-number-partner-onboarding">{currentStep > 2 ? <FontAwesomeIcon icon={faCheck} /> : '2'}</div>
            <div className="step-label-partner-onboarding">Form</div>
          </div>
          <div className="progress-line-partner-onboarding"></div>
          <div className={`progress-step-partner-onboarding ${currentStep >= 3 ? 'active-partner-onboarding' : ''} ${currentStep > 3 ? 'completed-partner-onboarding' : ''}`}>
            <div className="step-number-partner-onboarding">{currentStep > 3 ? <FontAwesomeIcon icon={faCheck} /> : '3'}</div>
            <div className="step-label-partner-onboarding">Templates</div>
          </div>
          <div className="progress-line-partner-onboarding"></div>
          <div className={`progress-step-partner-onboarding ${currentStep >= 4 ? 'active-partner-onboarding' : ''} ${currentStep > 4 ? 'completed-partner-onboarding' : ''}`}>
            <div className="step-number-partner-onboarding">{currentStep > 4 ? <FontAwesomeIcon icon={faCheck} /> : '4'}</div>
            <div className="step-label-partner-onboarding">Complete</div>
          </div>
        </div>

        <div className="content-partner-onboarding">
          {currentStep === 1 && (
            <div className="step-content-partner-onboarding">
              <h3>Step 1: Create Partner</h3>
              <p className="step-description-partner-onboarding">Enter the partner details</p>
              
              <div className="contact-info-partner-onboarding">
                <h4 className="contact-info-header-partner-onboarding">Partner Information</h4>
                
                <div className="form-group-partner-onboarding">
                  <label>Legal Company Name *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      setPartnerName(e.target.value);
                    }}
                    placeholder="e.g., SnapWave Media LLC"
                    disabled={loading}
                  />
                  <small>This will be used for all agreements and as the partner name</small>
                </div>
                
                <div className="form-group-partner-onboarding">
                  <label>Contact Person Name</label>
                  <input
                    type="text"
                    value={partnerContactPerson}
                    onChange={(e) => setPartnerContactPerson(e.target.value)}
                    placeholder="e.g., John Smith"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group-partner-onboarding">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={partnerEmail}
                    onChange={(e) => setPartnerEmail(e.target.value)}
                    placeholder="e.g., contact@partner.com"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group-partner-onboarding">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={partnerPhone}
                    onChange={(e) => setPartnerPhone(e.target.value)}
                    placeholder="e.g., (555) 123-4567"
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="agency-settings-partner-onboarding">
                <label className="agency-checkbox-label-partner-onboarding">
                  <input
                    type="checkbox"
                    checked={noDirectClientMessages}
                    onChange={(e) => setNoDirectClientMessages(e.target.checked)}
                    disabled={loading}
                    className="agency-checkbox-input-partner-onboarding"
                  />
                  <span className="agency-checkbox-text-partner-onboarding">No direct client messages</span>
                </label>
                <small className="agency-description-partner-onboarding">
                  When enabled, all mass messages and email notifications will be sent to the agency contact instead of individual clients
                </small>
              </div>
              
              {noDirectClientMessages && (
                <div className="agency-contact-fields-partner-onboarding">
                  <h4 className="agency-contact-header-partner-onboarding">Agency Contact Information</h4>
                  
                  <div className="form-group-partner-onboarding">
                    <label>Contact Person Name *</label>
                    <input
                      type="text"
                      value={agencyContactPerson}
                      onChange={(e) => setAgencyContactPerson(e.target.value)}
                      placeholder="e.g., John Smith"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group-partner-onboarding">
                    <label>Contact Email *</label>
                    <input
                      type="email"
                      value={agencyContactEmail}
                      onChange={(e) => setAgencyContactEmail(e.target.value)}
                      placeholder="e.g., contact@agency.com"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group-partner-onboarding">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      value={agencyContactPhone}
                      onChange={(e) => setAgencyContactPhone(e.target.value)}
                      placeholder="e.g., (555) 123-4567"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="step-content-partner-onboarding">
              <h3>Step 2: Application Form</h3>
              <p className="step-description-partner-onboarding">This form link should be sent to your clients to sign up for our Snapchat services</p>
              
              <div className="form-link-section-partner-onboarding">
                <label className="form-link-label-partner-onboarding">🔗 Your Form Link:</label>
                <div className="form-link-box-partner-onboarding">
                  <a 
                    href={`https://track.snapped.cc/apply/${previewSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="form-link-url-partner-onboarding"
                  >
                    https://track.snapped.cc/apply/{previewSlug}
                  </a>
                </div>
                
                <button
                  type="button"
                  className="email-link-button-partner-onboarding"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const sendEmail = async () => {
                      setLoading(true);
                      try {
                        const formUrl = `https://track.snapped.cc/apply/${previewSlug}`;
                        const subject = `${companyName} - Snapchat Sign Up Form`;
                        const body = `Hi,\n\nHere is your sign up form link for our Snapchat services:\n\n${formUrl}\n\nPlease complete this form to get started.\n\nBest regards,\nSnapped Team`;
                        
                        console.log('Sending email to:', partnerEmail);
                        
                        const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/messaging/send-to-conversation`, {
                          thread_id: partnerEmail,
                          message_text: body,
                          subject: subject,
                          platform: 'email',
                          contact_name: partnerContactPerson || companyName
                        });
                        
                        console.log('Email response:', response.data);
                        
                        if (response.data.status === 'success') {
                          toast.success(`Email sent to ${partnerEmail}`);
                        } else {
                          toast.error('Failed to send email');
                        }
                      } catch (error) {
                        console.error('Error sending email:', error);
                        toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
                      } finally {
                        setLoading(false);
                      }
                    };
                    
                    sendEmail();
                    return false;
                  }}
                  disabled={!partnerEmail || loading}
                >
                  {loading ? '📧 Sending...' : '📧 Send Form Link via Email'}
                </button>
                
                {!partnerEmail && (
                  <small className="email-hint-partner-onboarding">
                    No email entered in Step 1 - you can still copy the link above
                  </small>
                )}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="step-content-partner-onboarding">
              <h3>Step 3: Copy Contract Templates</h3>
              <p className="step-description-partner-onboarding">Select templates to copy and customize for this partner</p>
              
              {loading ? (
                <div className="loading-state-partner-onboarding">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <p>Loading templates...</p>
                </div>
              ) : (
                <div className="templates-list-partner-onboarding">
                  {availableTemplates.length === 0 ? (
                    <p className="no-templates-partner-onboarding">No templates available to copy</p>
                  ) : (
                    availableTemplates.map(template => (
                      <div key={template.id} className="template-item-partner-onboarding">
                        <label className="template-checkbox-partner-onboarding">
                          <input
                            type="checkbox"
                            checked={selectedTemplates.some(t => t.id === template.id)}
                            onChange={() => handleTemplateSelection(template)}
                            disabled={loading}
                          />
                          <div className="template-info-partner-onboarding">
                            <div className="template-title-partner-onboarding">{template.title}</div>
                            {template.description && (
                              <div className="template-description-partner-onboarding">{template.description}</div>
                            )}
                            <div className="template-preview-partner-onboarding">
                              Will be copied as: <strong>{template.title.replace(/TEMPLATE/gi, partnerName)}</strong>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="step-content-partner-onboarding">
              <h3>Step 4: Review & Complete</h3>
              <p className="step-description-partner-onboarding">Review your partner setup</p>
              
              <div className="review-section-partner-onboarding">
                <div className="review-item-partner-onboarding">
                  <label>Partner:</label>
                  <span>{partnerName}</span>
                </div>
                <div className="review-item-partner-onboarding">
                  <label>Company Name:</label>
                  <span>{companyName}</span>
                </div>
                <div className="review-item-partner-onboarding">
                  <label>Form:</label>
                  <span>{formName}</span>
                </div>
                <div className="review-item-partner-onboarding">
                  <label>Copied Templates:</label>
                  <ul className="copied-templates-list-partner-onboarding">
                    {copiedTemplates.map(template => (
                      <li key={template.id}>
                        <FontAwesomeIcon icon={faCheck} className="success-icon-partner-onboarding" />
                        {template.title}
                        <small>(from {template.original})</small>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="success-message-partner-onboarding">
                <FontAwesomeIcon icon={faCheck} />
                <p>Partner onboarding is ready to complete!</p>
              </div>
            </div>
          )}
        </div>

        <div className="footer-partner-onboarding">
          {currentStep > 1 && currentStep < 4 && (
            <button
              onClick={handleBack}
              className="btn-secondary-partner-onboarding"
              disabled={loading}
            >
              Back
            </button>
          )}
          
          <div className="footer-spacer-partner-onboarding"></div>
          
          {currentStep < 4 ? (
            <button
              onClick={() => {
                if (currentStep === 1) handleStep1Submit();
                else if (currentStep === 2) handleStep2Submit();
                else if (currentStep === 3) handleStep3Submit();
              }}
              className="btn-primary-partner-onboarding"
              disabled={loading || !canProceed()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                </>
              ) : (
                'Next'
              )}
            </button>
          ) : (
            <button
              onClick={handleStep4Submit}
              className="btn-primary-partner-onboarding btn-success-partner-onboarding"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Completing...
                </>
              ) : (
                'Complete Setup'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerOnboardingModal;
